import type { ElementType } from 'react';

import type { BadgeVariant } from '@picsart/design-system/components/Badge';
import type { CollapseStates, FloatingPlacement, StaticPlacement } from '@picsart/design-system/components/EditorSlot';
import type { IRGB } from '@picsart/web-layering/types';
import type { Credits } from '@picsart/miniapp-sdk-types';

import type { SuggestedBgResult } from 'services/pluggableApis/AIBackgroundsApis/types';
import type { BatchDataStatus, SuccessModalSourceEnum } from 'configs/constants';
import type { ICropTemplateValue, IResizeParams, IWatermarkAttrs } from 'pages/Batch/types';
import type { BATCH_TOOLS, ToolMonetization } from 'pages/Batch/configs';

import type { IWfxEffect } from 'pages/Batch/sections/Sidebar/WfxTool/constants';
import type { ObjectPosition } from 'pages/Batch/sections/Sidebar/EditorRemoveBgTool/MobilePanel/constants';
import type { ICropPatch } from '../index';

export type StatusIndicatorMode = 'both' | 'loadingOnly' | 'doneOnly' | 'none';

export type GridDensity = 'small' | 'medium' | 'large';

export interface BottomSheetConfig {
  behavior: 'floating' | 'static';
  collapseStates: FloatingPlacement | StaticPlacement;
  defaultSnapPoint: CollapseStates;
}

export interface IPreviewImage {
  itemKey: string;
  src: string;
  secondSrc: string | null;
}

export interface IUIState {
  successModal: {
    isOpen: boolean;
    description?: string;
    source?: SuccessModalSourceEnum | '';
    driveLink?: string | null;
    analyticsData?: object;
  };
  isShowCloseModal: boolean;
  isOpenedFromCloseModal: boolean;
  previewImage?: IPreviewImage;
  gridDensity: GridDensity;
}

export interface ICanvasSize {
  id: string;
  name: string;
  preview_url: string;
  type: number;
  size: {
    width: number;
    height: number;
  };
}

export interface SimpleTextType {
  id?: number;
  title: string;
  text: string;
  className: string;
  name: string;
}

// batch
export interface IBatchImage {
  src: string;
  width: number;
  height: number;
  removedBgSrc?: string;
  type?: string;
  mask?: string;
  enhancedSrc?: string;
}
export interface IBatchFileImage extends IBatchImage {
  blob?: File | Blob;
}

export interface IAsyncJob {
  task: string;
  version?: string;
  taskId: string;
  useWorkflowAPI?: boolean;
  toolAction: string;
}

export interface IBatchData {
  key: string;
  isProcessed: boolean;
  source: IBatchImage;
  result: IBatchImage;
  pendingResult?: IBatchImage;
  status: BatchDataStatus;
  statusIndicatorMode: StatusIndicatorMode;
  errorType?: string;
  asyncJob?: IAsyncJob;
  lastToolAction?: string;
  name?: string;
}

export interface IBatchFileData extends Omit<IBatchData, 'source' | 'result'> {
  source: IBatchFileImage;
  result: IBatchFileImage;
}

export interface IBatchTool {
  id: number;
  type: BATCH_TOOLS;
  title: string;
  icon: ElementType;
  dataTestId?: string;
  badge?: BadgeVariant | undefined;
  monetization: ToolMonetization;
  bottomSheetConfig: BottomSheetConfig;
  isWithoutBackground?: boolean;
}

export interface IUserBackground {
  url: string;
}

export interface ICropAttrsBatch extends ICropPatch {
  initWidth: number;
  initHeight: number;
  key: string;
  fireUpdate?: boolean;
}

export interface ICropFlip {
  x: number;
  y: number;
}

export interface ICropApplyAttrs {
  attrs: ICropAttrsBatch[];
  rotate: number;
  flip: ICropFlip;
  statusIndicatorMode?: StatusIndicatorMode;
}

export interface IEnhanceApplyAttrs {
  enhanceMapping: Record<string, string>;
  intensity: number;
  statusIndicatorMode?: StatusIndicatorMode;
}
export interface ICustomImage {
  id: string;
  url: string;
  [key: string]: any;
}

export interface IBgToolRatio {
  id: string;
  width: number;
  height: number;
}

// tx/ty are fractions of the natural image dimensions (so they apply uniformly in CSS-% and canvas-px contexts).
// rotation is in degrees, normalized to [0, 360).
export interface IForegroundTransform {
  tx: number;
  ty: number;
  sx: number;
  sy: number;
  rotation: number;
}

export const IDENTITY_FOREGROUND_TRANSFORM: IForegroundTransform = { tx: 0, ty: 0, sx: 1, sy: 1, rotation: 0 };

export const isIdentityForegroundTransform = (t: IForegroundTransform | undefined | null): boolean => !t || (t.tx === 0 && t.ty === 0 && t.sx === 1 && t.sy === 1 && t.rotation === 0);

// Non-transparent content bbox of a cutout PNG, as 0..1 fractions of natural image dims.
export interface IForegroundContentBbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export const FULL_CONTENT_BBOX: IForegroundContentBbox = { minX: 0, minY: 0, maxX: 1, maxY: 1 };

// Per-item settings snapshots: what each item's preview currently shows, so Apply bakes per-item state
// (each subset keeps its own settings) instead of re-applying the latest global settings to everyone.
// Written for the live selection on every settings change; read per item at preview + apply time, with
// a fallback to the current global value for keys without a snapshot.
export interface IWatermarkItemSetting {
  attrs: IWatermarkAttrs;
  logoId: string;
}
export interface IBackgroundItemSetting {
  color?: string | null;
  customBgUrl?: string | null;
  objectPosition?: ObjectPosition | null;
  ratio?: IBgToolRatio | null;
}
export type IResizeItemSetting = IResizeParams & { bgColor?: string | null };

export interface IAiEditParams {
  prompt: string;
  modelId: string;
  aspectRatio: string | null;
  referenceUrl: string | null;
}

export interface IBatchState {
  eraserItem: IBatchData | null;
  activeCustomBg: IUserBackground | null;
  activeColor: IRGB | null;
  objectPosition: ObjectPosition | null;
  bgToolRatio: IBgToolRatio | null;
  cropAlign: string | null;
  cropOrientation: string;
  cropTemplate: ICropTemplateValue;
  cropAttrs: ICropAttrsBatch[];
  cropRotate: number;
  cropFlip: ICropFlip;
  enhanceMapping: Record<string, string>;
  userWatermarks: ICustomImage[];
  backgroundCustomImages: ICustomImage[];
  enhanceBeforeAfter: number | null;
  latestAppliedTool: string | null;
  hasUsedSubscriptionTool: boolean;
  isToolApplying: boolean;
  generatedBackgrounds: ICustomImage[];
  suggestedBackgrounds: SuggestedBgResult[];
  creditsData: Credits;
  previewCollection: { [key: string]: { items: IBatchData[]; prompt?: string } };
  activePreviewItem: IBatchData | null;
  toolPrice: number;
  wfxSelectedEffect: IWfxEffect | null;
  wfxActiveTab: 'effects' | 'overlays';
  wfxSelectedOverlay: string | null;
  wfxShowSettings: boolean;
  wfxParamValues: Record<string, number>;
  wfxProcessAttrs: {
    effect: IWfxEffect | null;
    editorLibCategoryId: string | null;
    params: Record<string, number>;
    overlayResourceUrl: string | null;
    overlayBlendMode: string;
    overlayIntensity: number;
  };
  adjustProcessAttrs: {
    params: Record<string, number>;
  };
  // Params of the last applied AI Editor generation, recorded as a preset step on Apply.
  aiEditParams: IAiEditParams | null;
  lastAIBGPrompt: string | null;
  enhanceSelectedSmartAdjustment: string | null;
  enhanceSelectedBackgroundBlur: string | null;
  enhanceSmartAdjustmentIntensity: number;
  enhanceBackgroundBlurIntensity: number;
  enhanceActiveSection: 'smartAdjustment' | 'backgroundBlur' | null;
  enhanceActiveSubtool: string | null;
  enhanceItemIntensities: Record<string, number>;
  foregroundTransforms: Record<string, IForegroundTransform>;
  foregroundContentBboxes: Record<string, IForegroundContentBbox>;
  watermarkItemSettings: Record<string, IWatermarkItemSetting>;
  backgroundItemSettings: Record<string, IBackgroundItemSetting>;
  resizeItemSettings: Record<string, IResizeItemSetting>;
}
